import React, { useEffect, useMemo, useState } from 'react'
import ReactPlayer from 'react-player'
import { useRouter } from 'next/router'
import { Video, VideoProps } from 'primitives/Video'
import { NoAssetFallback } from 'common/NoAssetFallback'
import { VideoAsset } from 'graphql/types'
import { Icon } from 'common/Icon'
import { VideoAssetPlayButton } from './VideoAssetPlayButton'
import { VideoAssetControl } from './VideoAssetControl'
import { Text } from 'primitives/Text'
import { Duration } from 'luxon'

export interface VideoMediaTileProps {
  initDuration: number
  media: VideoAsset
  state: 'playing' | 'paused' | 'unmounted' | 'error'
  isVisible: boolean
  isMobile: boolean
  isPostModalOpen: boolean
  muted: boolean
  tileHeight?: string
  onError: VideoProps['onError']
  onProgress: VideoProps['onProgress']
  onMute: () => void
  onFullscreen: () => void
  onStart: () => void
  onPause: () => void
  onReady?: (player: ReactPlayer) => void
}
export const VideoMediaTile: React.FC<VideoMediaTileProps> = ({
  initDuration,
  media,
  state,
  isVisible,
  isMobile,
  isPostModalOpen,
  muted,
  onFullscreen,
  onMute,
  tileHeight = '100%',
  onError,
  onStart,
  onPause,
  onReady,
  ...videoProps
}) => {
  const { pathname } = useRouter()
  const [playing, setPlaying] = useState(
    // Desktop requires play button click as there can be multiple posts intersecting
    isMobile
      ? state === 'playing'
      : // play immediately if restoring from a saved duration
        (pathname === '/post/[postId]' && initDuration > 0) || false
  )
  const canPlay = state === 'paused' || state === 'playing'
  const [duration, setDuration] = useState(media?.duration)

  const srcSets = useMemo(
    () => [
      {
        src: media.url,
        type: 'video/webm',
      },
      {
        src: media.fallbackUrl,
        type: 'video/mp4',
      },
    ],
    [media.url, media.fallbackUrl]
  )

  const formatDuration = (duration: number): string => {
    switch (true) {
      // Avoid render delay of 0:00 when starting playing
      case playing && initDuration == 0:
        return '0:00'
      case duration > 60:
        return Duration.fromMillis(duration * 1000).toFormat('mm:ss')
      // If duration is 9.6, toFixed will change it to 10 resulting in 0:010
      case duration < 9.5:
        return '0:0' + duration.toFixed(0)
      default:
        return '0:' + duration.toFixed(0).toString()
    }
  }

  useEffect(() => {
    if (isMobile) {
      setPlaying(state === 'playing')
    } else {
      if (playing && state !== 'playing') {
        setPlaying(false)
      }
    }
  }, [playing, state, isMobile])

  const renderMedia = () => {
    switch (true) {
      // The purpose of this state is to save user bandwidth by not streaming videos that are not being viewed.
      case state === 'unmounted':
        return <></>
      case canPlay:
        return (
          <Video
            playsinline
            loop
            muted={muted}
            controls={false}
            url={srcSets}
            playing={playing}
            initDuration={initDuration}
            onPause={() => {
              setPlaying(false)
              onPause()
            }}
            onReady={onReady}
            onError={onError}
            onStart={onStart}
            onInit={setDuration}
            {...videoProps}
          />
        )
      case state === 'error':
        return (
          <NoAssetFallback
            assetType='video'
            css={{
              width: '100%',
              paddingBottom: tileHeight,
            }}
          />
        )
    }
  }

  return (
    <>
      {renderMedia()}
      {!isMobile && canPlay && (
        <VideoAssetPlayButton
          preventPropagation={true}
          show={!playing}
          onClick={() => {
            setPlaying(true)
          }}
        />
      )}
      <VideoAssetControl
        show={playing}
        css={{
          left: isMobile && isPostModalOpen ? '25px' : '14px',
          top: isMobile && isPostModalOpen ? '114px' : '',
          bottom: isMobile && isPostModalOpen ? '' : '14px',
        }}
        onClick={onMute}
      >
        <Icon icon={muted ? 'Muted' : 'Speaker'} />
      </VideoAssetControl>
      <VideoAssetControl
        show={canPlay}
        css={{
          top: isPostModalOpen && isMobile ? '19px' : '14px',
          right: isPostModalOpen && isMobile ? '52px' : '14px',
          borderRadius: '8px',
          width: 'auto',
          padding: '6px',
        }}
      >
        {duration && (
          <Text
            type='body3'
            css={{
              color: '$gray800 !important',
              cursor: 'default',
            }}
          >
            {/* Display the full duration of the video only before the user clicks the first time the play icon. */}
            {initDuration > 0
              ? formatDuration(initDuration)
              : formatDuration(duration)}
          </Text>
        )}
      </VideoAssetControl>
      <VideoAssetControl
        show={canPlay}
        css={{
          left: isMobile && isPostModalOpen ? '25px' : '',
          right: isMobile && isPostModalOpen ? '' : '14px',
          top: isMobile && isPostModalOpen ? '72px' : '',
          bottom: isMobile && isPostModalOpen ? '' : '14px',
        }}
        onClick={onFullscreen}
      >
        <Icon icon='Fullscreen' />
      </VideoAssetControl>
    </>
  )
}
